import { StandardMainPanel } from "../../../../../../../shared/components/forms/StandardMainPanel"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import React, { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { AutoRefillFormValues, RefillAmount, UserWallet, WalletsOrderTypeRefill } from "../refillTypes"
import { Divider, Grid, ListItemButton, MenuItem, Stack, Typography } from "@mui/material"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { Form, Formik } from "formik"
import * as yup from "yup"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { createWalletsRefill, WalletsRefill } from "../refillApi"
import { PaymentMethodType } from "../../../../../../payments/paymentTypes"
import { amountToNumber } from "../helpers"
import { useParams } from "react-router-dom"
import { ActivateAutoTopSuccessDialog } from "./ActivatedTopUpSuccessDialog"
import { UserCardView } from "./SelectWalletDialog"
import { isEmpty } from "lodash"
import { FormikDropdown, FormikTextField } from "../../../../../../../shared/components/forms/Formik"
import { iterateStringEnum } from "../../../../../../../lib/enums"
import { StandardPaymentMethodIcon } from "../../../../../../../shared/components/payment/StandardPaymentMethodIcon"
import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero, toDineroOrUndefined } from "../../../../../../currency/currencyLib"
import { INVALID_INPUT_KEY, MAX_INPUT_REFILL_LENGTH, MAX_WALLET_BALANCE } from "./WalletsRefillManual"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../../context/contextSelectors"

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
    listItemButton: {
        padding: "1rem",
        justifyContent: "space-between",
        "&:hover": {
            background: tailwindPalette.slate[100],
        },
    },
    paymentMethodContainer: {
        width: "20rem",
    },
    paymentMethodSelected: {
        background: tailwindPalette.slate[100],
    },
    button: {
        width: "20rem",
    },
}))

type Props = Readonly<{
    walletDetails: UserWallet
}>

export function WalletsRefillAuto({ walletDetails }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const { walletId } = useParams()
    const userId = useAppSelector(selectEffectiveUserId) || 0
    const isAutomatic = !isEmpty(walletDetails.paymentDetails.refill?.automatic?.subscription)
    const moneyFactory = useMoney({})

    const [showActivateSuccessDialog, setShowActivateSuccessDialog] = useState(false)

    const defaultInitialValues: AutoRefillFormValues = {
        paymentMethod: "",
        amount: "",
        customAmount: "",
        threshold: "",
    }

    const validationSchema = yup.object({
        paymentMethod: yup.string().required(l10n.getString("required-field")),
        amount: yup.string().required(l10n.getString("required-field")),
        customAmount: yup
            .number()
            .notOneOf([0], l10n.getString("user-wallets-refill-manual-main-amount-zero-error"))
            .when(["amount", "threshold"], ([amount, threshold], schema) => {
                const thresholdNumber = Number(threshold) || 0
                const maxValue = MAX_WALLET_BALANCE - thresholdNumber

                if (amount === RefillAmount.AMOUNT_OTHER) {
                    if (maxValue <= 0) {
                        return schema
                            .required(l10n.getString("required-field"))
                            .max(
                                0,
                                l10n.getString("user-wallets-refill-manual-main-amount-max-error", {
                                    maxRefillAmount: moneyFactory.format(
                                        toDinero({
                                            amount: 0,
                                            currency: "DKK",
                                            scale: 0,
                                        }),
                                    ),
                                }),
                            )
                            .test(
                                "must-be-zero",
                                l10n.getString("user-wallets-refill-manual-main-amount-max-error", {
                                    maxRefillAmount: moneyFactory.format(
                                        toDinero({
                                            amount: 0,
                                            currency: "DKK",
                                            scale: 0,
                                        }),
                                    ),
                                }),
                                (value) => value === 0,
                            )
                    }

                    return schema.required(l10n.getString("required-field")).max(
                        maxValue,
                        l10n.getString("user-wallets-refill-manual-main-amount-max-error", {
                            maxRefillAmount: moneyFactory.format(
                                toDinero({
                                    amount: maxValue,
                                    currency: "DKK",
                                    scale: 0,
                                }),
                            ),
                        }),
                    )
                }

                return schema.notRequired()
            }),

        threshold: yup
            .number()
            .max(
                MAX_WALLET_BALANCE - 1,
                l10n.getString("user-wallets-refill-auto-amount-max-threshold-error", {
                    maxThreshold: moneyFactory.format(
                        toDinero({
                            amount: MAX_WALLET_BALANCE - 1,
                            currency: "DKK",
                            scale: 0,
                        }),
                    ),
                }),
            )
            .required(l10n.getString("required-field")),
    })

    const { mutate } = useApiMutation(createWalletsRefill, {
        async onSuccess(_, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [WalletsRefill.WALLET_REFILL_DETAILS] })
            setShowActivateSuccessDialog(true)
        },
    })

    function handleSubmit(values: AutoRefillFormValues) {
        mutate({
            userId,
            acquirerSetupId: walletDetails.acquirer.acquirerSetupId,
            walletId: walletDetails.id ? walletDetails.id : 0,
            order: {
                orderType: WalletsOrderTypeRefill.REFILL_SUBSCRIPTION,
                payment: {
                    method: values.paymentMethod as PaymentMethodType,
                },
                refillDetails: {
                    amount: {
                        currency: "DKK",
                        amount: +values.customAmount || amountToNumber(values.amount),
                        scale: 0,
                    },
                    threshold: {
                        currency: "DKK",
                        amount: +values.threshold,
                        scale: 0,
                    },
                },
            },
        })
    }

    const subscriptionThreshold = toDineroOrUndefined(
        walletDetails.paymentDetails.refill.automatic?.subscription?.order.refillDetails.threshold,
    )
    const subscriptionAmount = toDineroOrUndefined(
        walletDetails.paymentDetails.refill.automatic?.subscription?.order.refillDetails.amount,
    )

    return (
        <Formik
            enableReinitialize
            initialValues={defaultInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue, errors }) => (
                <Form>
                    <Grid container spacing="1.5rem" columns={{ xs: 8, md: 12 }}>
                        <Grid item xs={8} md={8}>
                            <StandardMainPanel>
                                <Stack direction="column" spacing="2rem">
                                    <Stack direction="column" spacing="1rem">
                                        <LocalizedStrict id="user-wallets-refill-auto-activate-modal-threshold-subtitle">
                                            <Typography variant="subtitle1">When balance falls below</Typography>
                                        </LocalizedStrict>
                                        {isAutomatic ? (
                                            <Typography>
                                                {subscriptionThreshold
                                                    ? moneyFactory.format(subscriptionThreshold)
                                                    : ""}
                                            </Typography>
                                        ) : (
                                            <LocalizedStrict
                                                id="user-wallets-refill-auto-activate-modal-threshold-placeholder"
                                                attrs={{ label: true }}
                                            >
                                                <FormikTextField
                                                    className={classes.input}
                                                    fullWidth
                                                    type="number"
                                                    name="threshold"
                                                    label="Amount (DKK)"
                                                    inputProps={{
                                                        inputMode: "numeric",
                                                        maxLength: MAX_INPUT_REFILL_LENGTH,
                                                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                                            if (INVALID_INPUT_KEY.includes(e.key)) {
                                                                e.preventDefault()
                                                            }
                                                        },
                                                        onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                                            const currentValue = e.currentTarget.value

                                                            const cleanValue = currentValue.replace(/[^0-9]/g, "")

                                                            const limitedValue = cleanValue.slice(
                                                                0,
                                                                MAX_INPUT_REFILL_LENGTH,
                                                            )

                                                            if (currentValue !== limitedValue) {
                                                                e.currentTarget.value = limitedValue
                                                                setFieldValue("customAmount", limitedValue)
                                                            }
                                                        },
                                                    }}
                                                />
                                            </LocalizedStrict>
                                        )}
                                    </Stack>
                                    <Stack direction="column" spacing="1rem">
                                        <LocalizedStrict id="user-wallets-refill-auto-activate-modal-refill-subtitle">
                                            <Typography variant="subtitle1">Refill with amount</Typography>
                                        </LocalizedStrict>
                                        {isAutomatic ? (
                                            <Typography>
                                                {subscriptionAmount ? moneyFactory.format(subscriptionAmount) : ""}
                                            </Typography>
                                        ) : (
                                            <LocalizedStrict
                                                id="user-wallets-refill-manual-main-amount-label"
                                                attrs={{ label: true }}
                                            >
                                                <FormikDropdown
                                                    className={classes.input}
                                                    onChange={() => setFieldValue("customAmount", "")}
                                                    name="amount"
                                                    label="Amount"
                                                >
                                                    {[...iterateStringEnum(RefillAmount)].map((value) => (
                                                        <MenuItem value={value} key={value}>
                                                            <LocalizedEnum
                                                                base="user-wallets-refill-manual-main-amount"
                                                                enumValue={value}
                                                            >
                                                                <span>{value}</span>
                                                            </LocalizedEnum>
                                                        </MenuItem>
                                                    ))}
                                                </FormikDropdown>
                                            </LocalizedStrict>
                                        )}
                                    </Stack>
                                    {values.amount === RefillAmount.AMOUNT_OTHER && (
                                        <LocalizedStrict
                                            id="user-wallets-refill-manual-main-custom-amount-label"
                                            attrs={{ label: true }}
                                        >
                                            <FormikTextField
                                                type="number"
                                                className={classes.input}
                                                fullWidth
                                                inputProps={{
                                                    maxLength: 4,
                                                    inputMode: "numeric",
                                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (INVALID_INPUT_KEY.includes(e.key)) {
                                                            e.preventDefault()
                                                        }
                                                    },
                                                    onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                                        const currentValue = e.currentTarget.value

                                                        const cleanValue = currentValue.replace(/[^0-9]/g, "")

                                                        const limitedValue = cleanValue.slice(
                                                            0,
                                                            MAX_INPUT_REFILL_LENGTH,
                                                        )

                                                        if (currentValue !== limitedValue) {
                                                            e.currentTarget.value = limitedValue
                                                            setFieldValue("customAmount", limitedValue)
                                                        }
                                                    },
                                                }}
                                                name="customAmount"
                                                label="Custom amount"
                                            />
                                        </LocalizedStrict>
                                    )}
                                    <Stack className={classes.paymentMethodContainer} direction="column">
                                        <LocalizedStrict id="user-wallets-refill-manual-main-payment-method">
                                            <Typography variant="subtitle1" mb="1rem">
                                                Payment methods:
                                            </Typography>
                                        </LocalizedStrict>
                                        {walletDetails.paymentDetails.refill?.automatic?.paymentMethods?.map(
                                            (paymentMethod) => (
                                                <React.Fragment key={paymentMethod.value}>
                                                    <Divider />
                                                    <ListItemButton
                                                        onClick={() => {
                                                            setFieldValue("paymentMethod", paymentMethod.value)
                                                        }}
                                                        className={cx(classes.listItemButton, {
                                                            [classes.paymentMethodSelected]:
                                                                values.paymentMethod === paymentMethod.value,
                                                        })}
                                                    >
                                                        <LocalizedEnum
                                                            base="purchases-payment-method"
                                                            enumValue={paymentMethod.value}
                                                        >
                                                            <span>{paymentMethod.value}</span>
                                                        </LocalizedEnum>
                                                        <StandardPaymentMethodIcon icon={paymentMethod.icon} />
                                                    </ListItemButton>
                                                </React.Fragment>
                                            ),
                                        )}
                                        <Divider />
                                        {errors?.paymentMethod && (
                                            <LocalizedStrict id="user-wallets-refill-payment-method-error">
                                                <Typography p=".5rem 1rem" color="error" fontSize="0.75rem">
                                                    Payment method is required
                                                </Typography>
                                            </LocalizedStrict>
                                        )}
                                    </Stack>
                                    {isAutomatic ? (
                                        <Stack direction="row" spacing="1rem">
                                            <LocalizedStrict id="user-wallets-refill-auto-activate-modal-delete-button">
                                                <StandardButton fullWidth={false} color="error" variant="contained">
                                                    Delete auto refill
                                                </StandardButton>
                                            </LocalizedStrict>
                                            <LocalizedStrict id="user-wallets-refill-auto-activate-modal-update-button">
                                                <StandardButton fullWidth={false} variant="contained">
                                                    Edit auto refill
                                                </StandardButton>
                                            </LocalizedStrict>
                                        </Stack>
                                    ) : (
                                        <LocalizedStrict id="user-wallets-refill-auto-activate-modal-submit-button">
                                            <StandardButton
                                                className={classes.button}
                                                fullWidth={false}
                                                type="submit"
                                                variant="contained"
                                            >
                                                Activate auto refill
                                            </StandardButton>
                                        </LocalizedStrict>
                                    )}
                                </Stack>
                            </StandardMainPanel>
                        </Grid>
                        {walletDetails && (
                            <Grid item xs={8} md={4}>
                                <LocalizedStrict
                                    id={
                                        isAutomatic
                                            ? "user-wallets-refill-automatic-type"
                                            : "user-wallets-refill-manual-type"
                                    }
                                    attrs={{
                                        statusLabel: true,
                                    }}
                                >
                                    <UserCardView
                                        balance={walletDetails.balance}
                                        displayName={walletDetails.displayName}
                                        brandingLabel={walletDetails.brandingLabel}
                                        statusLabel={isAutomatic ? "Automatic" : "Manual"}
                                        onSelect={() => {}}
                                    />
                                </LocalizedStrict>
                            </Grid>
                        )}
                    </Grid>

                    {showActivateSuccessDialog && (
                        <ActivateAutoTopSuccessDialog
                            open={showActivateSuccessDialog}
                            onClose={() => setShowActivateSuccessDialog(false)}
                        />
                    )}
                </Form>
            )}
        </Formik>
    )
}
